import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { errorMessage, isProblemError } from "../api/errors";
import { cx } from "./cx";
import styles from "./Toast.module.css";

export type ToastTone = "neutral" | "success" | "warning" | "danger";

export type ToastOptions = {
  /** Ligne principale, courte. */
  title: ReactNode;
  /** Détail sous le titre : le `detail` d'un refus, un montant manquant. */
  description?: ReactNode;
  /** Défaut `neutral`. */
  tone?: ToastTone;
  /** Durée d'affichage en millisecondes. `0` pour rester jusqu'au clic. Défaut `5000`. */
  duration?: number;
};

type Toast = ToastOptions & { id: string; tone: ToastTone };

export type ToastApi = {
  /** Affiche un message. Rend son identifiant, pour pouvoir le retirer. */
  notify: (options: ToastOptions) => string;
  /**
   * Affiche l'erreur reçue d'un appel d'API, avec le bon ton et le bon texte.
   *
   * ```ts
   * const recu = await achat.run(…);
   * if (!recu) notifyError(achat.error, "L'achat a été refusé.");
   * ```
   */
  notifyError: (error: unknown, title?: ReactNode) => string;
  /** Retire un message avant la fin de son délai. */
  dismiss: (id: string) => void;
};

const ContexteToast = createContext<ToastApi | null>(null);

/**
 * Fournit la pile de messages. Posé une fois, autour de toute l'application.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [messages, setMessages] = useState<Toast[]>([]);
  const minuteurs = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    setMessages((actuels) => actuels.filter((m) => m.id !== id));
    const minuteur = minuteurs.current.get(id);
    if (minuteur !== undefined) {
      clearTimeout(minuteur);
      minuteurs.current.delete(id);
    }
  }, []);

  const notify = useCallback(
    (options: ToastOptions): string => {
      const id = crypto.randomUUID();
      const duree = options.duration ?? 5000;
      setMessages((actuels) => [
        ...actuels,
        { ...options, id, tone: options.tone ?? "neutral" },
      ]);
      if (duree > 0) {
        minuteurs.current.set(
          id,
          setTimeout(() => dismiss(id), duree),
        );
      }
      return id;
    },
    [dismiss],
  );

  const notifyError = useCallback(
    (error: unknown, title: ReactNode = "Opération refusée"): string =>
      notify({
        title,
        description: errorMessage(error),
        // Un refus métier (409, 422) est une information, pas une panne : le
        // ton d'avertissement le dit, et réserve le rouge aux vraies erreurs.
        tone: isProblemError(error) && error.status < 500 ? "warning" : "danger",
        // Une erreur reste plus longtemps : on la lit, parfois on la recopie.
        duration: 8000,
      }),
    [notify],
  );

  // Les minuteurs en cours ne doivent pas survivre au démontage de la racine.
  useEffect(() => {
    const enCours = minuteurs.current;
    return () => {
      enCours.forEach(clearTimeout);
      enCours.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(
    () => ({ notify, notifyError, dismiss }),
    [notify, notifyError, dismiss],
  );

  return (
    <ContexteToast.Provider value={api}>
      {children}
      {/* `polite` et non `assertive` : un message ne coupe pas la lecture en
          cours, il est annoncé à la première pause. */}
      <div className={styles.pile} role="status" aria-live="polite">
        {messages.map((message) => (
          <div
            key={message.id}
            className={cx(
              styles.message,
              message.tone !== "neutral" && styles[message.tone],
            )}
          >
            <div className={styles.contenu}>
              <span className={styles.titre}>{message.title}</span>
              {message.description !== undefined && (
                <span className={styles.description}>{message.description}</span>
              )}
            </div>
            <button
              type="button"
              className={styles.fermer}
              onClick={() => dismiss(message.id)}
              aria-label="Fermer ce message"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ContexteToast.Provider>
  );
}

/**
 * Accès à la pile de messages depuis n'importe quel écran.
 *
 * ```ts
 * const { notify, notifyError } = useToast();
 * ```
 */
export function useToast(): ToastApi {
  const api = useContext(ContexteToast);
  if (api === null) {
    throw new Error("useToast doit être appelé sous un <ToastProvider>.");
  }
  return api;
}
